import { lazy, Suspense, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import {
  Button,
  Heading,
  Label,
  LazyFloating,
  Popup,
  PlusIcon,
  showToast,
  LoadingSection,
} from '@quickmd-llc/design-system'
import { MEDICATIONS_ADD_ROUTE } from '@/utils/constants'
import { useGetAdminsMedications } from '@/api/generated/medication/medication'
import { useGetAdminsFulfillmentpartners } from '@/api/generated/fulfillment-partner/fulfillment-partner'
import { useDeleteMedication } from './hooks/useDeleteMedication'
import type {
  FulfillmentPartnerAdminApiContract,
  MedicationStaffApiOutContract,
} from '@/api/generated/pwrdbyQuickMDAdminsApi.schemas'
import type { ManageFulfillmentPartnersModalProps } from './components/modals/ManageFulfillmentPartnersModal'
import { MedicationListPanel } from './components/MedicationListPanel'
import { MedicationDetailPanel } from './components/MedicationDetailPanel'

const LazyFulfillmentPartnerFormModal = lazy(() =>
  import('./components/modals/FulfillmentPartnerFormModal').then((mod) => ({
    default: mod.FulfillmentPartnerFormModal,
  })),
)

export function MedicationsPage() {
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()

  const selectedId = searchParams.get('selected')
  const search = searchParams.get('search') ?? ''

  const { data: medications = [], isLoading } = useGetAdminsMedications()
  const { data: partners = [] } = useGetAdminsFulfillmentpartners()
  const { mutate: deleteMedication, isPending: isDeleting } = useDeleteMedication()

  function updateParams(updates: Record<string, string | null>) {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev)
        for (const [key, value] of Object.entries(updates)) {
          if (value) {
            next.set(key, value)
          } else {
            next.delete(key)
          }
        }
        return next
      },
      { replace: true },
    )
  }

  const [manageFulfillmentPartnersOpen, setManageFulfillmentPartnersOpen] = useState(false)
  const [partnerFormState, setPartnerFormState] = useState<
    { mode: 'add'; partner: null } | { mode: 'edit'; partner: FulfillmentPartnerAdminApiContract } | null
  >(null)
  const [medicationToDelete, setMedicationToDelete] = useState<MedicationStaffApiOutContract | null>(null)

  const selectedMedication = (selectedId ? medications.find((m) => m.Id === selectedId) : null) ?? null

  function handleDelete(medication: MedicationStaffApiOutContract) {
    setMedicationToDelete(medication)
  }

  function handleDeleteConfirm() {
    if (!medicationToDelete) return
    if (!medicationToDelete.Id) return
    deleteMedication(medicationToDelete.Id, {
      onSuccess: () => {
        updateParams({ selected: null })
        setMedicationToDelete(null)
        showToast({
          type: 'success',
          message: `${medicationToDelete.Name} deleted`,
          position: 'bottom-right',
        })
      },
    })
  }

  function handleAddPartner() {
    setManageFulfillmentPartnersOpen(false)
    setPartnerFormState({ mode: 'add', partner: null })
  }

  function handleEditPartner(partner: FulfillmentPartnerAdminApiContract) {
    setManageFulfillmentPartnersOpen(false)
    setPartnerFormState({ mode: 'edit', partner })
  }

  function handlePartnerFormClose() {
    setPartnerFormState(null)
    if (partners.length > 0) {
      setManageFulfillmentPartnersOpen(true)
    }
  }

  function handlePartnerSaved() {
    setPartnerFormState(null)
    setManageFulfillmentPartnersOpen(true)
  }

  function handlePartnerDeleted() {
    // Keep list modal open after delete
    setManageFulfillmentPartnersOpen(true)
  }

  const pageContent = (
    <div style={{ width: '100%' }}>
      <div className='grid h-lvh gap-sp-3 bg-background p-sp-6 [grid-template-rows:auto_1fr]'>
        {/* Page header — sits on gray bg, no white bar */}
        <div className='flex items-center justify-between'>
          <Heading level='h1'>Medications</Heading>
          <Button onClick={() => navigate(MEDICATIONS_ADD_ROUTE)} disabled={partners.length === 0}>
            <PlusIcon />
            Add medication
          </Button>
        </div>

        {/* 2-panel body */}
        <div className='flex min-h-0 flex-1 flex-col overflow-hidden'>
          {isLoading ?
            <LoadingSection />
          : <div className='grid h-full grid-cols-[304px_1fr] gap-sp-2'>
              <MedicationListPanel
                medications={medications}
                selectedId={selectedId}
                search={search}
                onSearchChange={(v) => updateParams({ search: v || null })}
                onSelect={(med) => updateParams({ selected: med.Id })}
                onManagePartners={() => {
                  if (partners.length === 0) {
                    setPartnerFormState({ mode: 'add', partner: null })
                  } else {
                    setManageFulfillmentPartnersOpen(true)
                  }
                }}
              />
              <MedicationDetailPanel
                hasAnyMedications={medications.length > 0}
                hasPartners={partners.length > 0}
                selectedMedication={selectedMedication}
                onDelete={handleDelete}
              />
            </div>
          }
        </div>
      </div>

      {/* Manage fulfillment partners */}
      <LazyFloating<ManageFulfillmentPartnersModalProps>
        loader={() =>
          import('./components/modals/ManageFulfillmentPartnersModal').then((mod) => ({
            default: mod.ManageFulfillmentPartnersModal,
          }))
        }
        isOpen={manageFulfillmentPartnersOpen}
        onClose={() => setManageFulfillmentPartnersOpen(false)}
        onAddPartner={handleAddPartner}
        onEditPartner={handleEditPartner}
        onPartnerDeleted={handlePartnerDeleted}
      />

      {/* Add / Edit fulfillment partner — conditionally rendered so form resets on close */}
      {partnerFormState ?
        <Suspense fallback={null}>
          <LazyFulfillmentPartnerFormModal
            isOpen
            onClose={handlePartnerFormClose}
            partner={partnerFormState.partner}
            onSaved={handlePartnerSaved}
          />
        </Suspense>
      : null}

      {/* Delete medication confirmation */}
      {medicationToDelete ?
        <Popup
          isOpen
          header='Delete medication'
          body={
            <span>
              Are you sure you want to delete <Label size='md'>{medicationToDelete.Name}</Label>? This action cannot be
              undone.
            </span>
          }
          footer={
            <div className='flex justify-end gap-sp-1'>
              <Button priority='secondary' onClick={() => setMedicationToDelete(null)} disabled={isDeleting}>
                Cancel
              </Button>
              <Button priority='primary' theme='danger' onClick={handleDeleteConfirm} loading={isDeleting}>
                Delete
              </Button>
            </div>
          }
          onClose={() => setMedicationToDelete(null)}
        />
      : null}
    </div>
  )

  return pageContent
}
